// What the map browser shows about a map without loading it: its timeruns
// and ETJump worldspawn rules, read from the BSP entity lump. Cached in memory
// and in localStorage (keyed by map and pk3), because reading a BSP out of a
// pk3 means inflating the whole file.

import type { VirtualFS } from '../../fs/vfs';
import { readEntityString } from '../../shared/bsp';
import { mapMetaFromEntities, type MapMeta } from '../../shared/mapInfo';

export { describeRules, mapMetaFromEntities, type MapMeta, type MapRulesInfo } from '../../shared/mapInfo';

const STORAGE_KEY = 'tjgame.mapmeta';
const memory = new Map<string, MapMeta>();
let stored: Record<string, MapMeta> | null = null;

function storedTable(): Record<string, MapMeta> {
  if (!stored) {
    try {
      stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as Record<string, MapMeta>;
    } catch {
      stored = {};
    }
  }
  return stored;
}

const cacheKey = (vfs: VirtualFS, map: string) => `${map}|${vfs.sourceOf(`maps/${map}.bsp`) ?? ''}`;

/** Cached info, if the map was already read. */
export function peekMapMeta(vfs: VirtualFS, map: string): MapMeta | undefined {
  const key = cacheKey(vfs, map);
  return memory.get(key) ?? storedTable()[key];
}

export async function getMapMeta(vfs: VirtualFS, map: string): Promise<MapMeta | null> {
  const known = peekMapMeta(vfs, map);
  if (known) return known;
  const data = await vfs.read(`maps/${map}.bsp`);
  if (!data) return null;
  let meta: MapMeta;
  try {
    meta = mapMetaFromEntities(readEntityString(data));
  } catch (err) {
    console.warn(`map info ${map}:`, err);
    return null;
  }
  const key = cacheKey(vfs, map);
  memory.set(key, meta);
  const table = storedTable();
  table[key] = meta;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(table));
  } catch {
    /* quota: the memory cache still works */
  }
  return meta;
}

/**
 * Reads the info of every map not known yet, one map at a time so the menu
 * stays responsive. `onUpdate` runs after each map; returns a cancel function.
 */
export function indexMaps(vfs: VirtualFS, maps: string[], onUpdate: () => void): () => void {
  let cancelled = false;
  (async () => {
    for (const map of maps) {
      if (cancelled) return;
      if (peekMapMeta(vfs, map)) continue;
      await new Promise((r) => setTimeout(r, 0));
      if (cancelled) return;
      if (await getMapMeta(vfs, map)) onUpdate();
    }
  })().catch((err) => console.warn('map index:', err));
  return () => {
    cancelled = true;
  };
}
